import type { ReactNode } from "react";
import { LoadingGhost } from "src/components/loading-ghost/LoadingGhost";
import { ErrorIndicator } from "src/components/error-indicator/ErrorIndicator";

const defaultVariants = {
	loadingGraphic: (
		<LoadingGhost />
	),
	errorGraphic: (state: StatefullState & { status: "rejected" }) => {
		const primaryError = state.errors[0];
		const errorCode = primaryError?.status ? `${primaryError.status}` : undefined;

		return (
			<ErrorIndicator errorCode={errorCode ?? undefined} />
		);
	},
} as const;

export type StatefullObject = {
	isError: boolean;
	isLoading: boolean;
	isUninitialized: boolean;
	isFetching: boolean;
	data?: unknown;
	error?: unknown; // RTK Query error can be FetchBaseQueryError | SerializedError
	_allowsNull?: true;
};

export type StatefullObjectMap = Record<string, StatefullObject>;

type StateHandlerProps<T extends StatefullObjectMap> = {
	statefullObjectMap: T;
	children: (
		data: {
			[K in keyof T]: T[K] extends { _allowsNull: true } ? T[K]["data"] : NonNullable<T[K]["data"]>;
		},
	) => ReactNode;
	loadingGraphic?: React.ReactNode;
	errorGraphic?:
		| React.ReactNode
		| ((errors: StatefullState & { status: "rejected" }) => React.ReactNode);
	showStaleDataWhileFetching?: boolean;
};

// More detailed state that matches Redux RTK Query statuses
type StatefullState =
	| { status: "uninitialized" }
	| { status: "pending"; isFetching: boolean }
	| { status: "fulfilled"; hasData: boolean }
	| {
			status: "rejected";
			errors: Array<{
				key: string;
				status?: number;
				message?: string;
				data?: any;
			}>;
	  };

function isInactiveState(obj: StatefullObject | null | undefined): boolean {
	if (!obj) {
		return true;
	}

	const hasError = Boolean(obj.isError);
	if (hasError) {
		return false;
	}

	const loading = Boolean(obj.isLoading);
	const fetching = Boolean(obj.isFetching);
	const uninitialized = Boolean(obj.isUninitialized);
	const hasData = obj.data !== undefined && obj.data !== null;

	return !loading && !fetching && uninitialized && !hasData;
}

export function StateHandler<T extends StatefullObjectMap>({
	statefullObjectMap,
	children,
	loadingGraphic,
	errorGraphic,
	showStaleDataWhileFetching,
}: StateHandlerProps<T>) {
	const state = GetState(statefullObjectMap, {
		allowStaleData: Boolean(showStaleDataWhileFetching),
	});

	switch (state.status) {
		case "rejected":
			if (typeof errorGraphic === "function") {
				return errorGraphic(state);
			}
			if (errorGraphic) {
				return errorGraphic;
			}
			return defaultVariants.errorGraphic(state);
		case "uninitialized":
		case "pending":
			return loadingGraphic ?? defaultVariants.loadingGraphic;
		case "fulfilled": {
			const dataMap = Object.fromEntries(
				Object.keys(statefullObjectMap).map((key) => {
					const obj = statefullObjectMap[key];
					const isAllowedToBeNull = obj._allowsNull === true;
					const isInactive = isInactiveState(obj);

					if (isAllowedToBeNull && isInactive) {
						return [key, null];
					} else {
						return [key, obj.data];
					}
				}),
			) as {
				[K in keyof T]: T[K] extends { _allowsNull: true } ? T[K]["data"] : NonNullable<T[K]["data"]>;
			};

			return <>{children(dataMap)}</>;
		}
	}
}

export function GetState(
	statefullMap: StatefullObjectMap,
	options?: { allowStaleData?: boolean },
): StatefullState {
	const statefullEntries = Object.entries(statefullMap);

	// Check for errors first and collect error details
	const errorObjects = statefullEntries.filter(([_, obj]) => obj.isError);
	if (errorObjects.length > 0) {
		const errors = errorObjects.map(([key, obj]) => {
			const err = obj.error as any;
			return {
				key,
				status: err?.status,
				message: err?.message || err?.data?.message || err?.error || `Error in ${key}`,
				data: err?.data,
			};
		});
		return { status: "rejected", errors };
	}

	const requiredObjects = statefullEntries.filter(([_, obj]) => !obj._allowsNull);

	// Check if any required objects are uninitialized
	if (requiredObjects.some(([_, obj]) => obj.isUninitialized)) {
		return { status: "uninitialized" };
	}

	// If allowing stale data and we have some data, consider it fulfilled
	if (options?.allowStaleData && statefullEntries.some(([_, obj]) => obj.data !== undefined)) {
		return { status: "fulfilled", hasData: true };
	}

	// Check if loading or fetching
	if (requiredObjects.some(([_, obj]) => obj.isLoading || obj.isFetching)) {
		const isFetching = requiredObjects.some(([_, obj]) => obj.isFetching);
		return { status: "pending", isFetching };
	}

	// All data is ready
	return { status: "fulfilled", hasData: true };
}

export function GetDataMap<T extends Record<string, StatefullObject>>(
	statefullMap: T,
	options?: { allowStaleData?: boolean },
): {
	[K in keyof T]: T[K] extends { _allowsNull: true } ? T[K]["data"] : NonNullable<T[K]["data"]>;
} {
	const state = GetState(statefullMap, options);

	if (state.status !== "fulfilled") throw new Error("Data is not ready");

	const dataMap = Object.fromEntries(
		Object.entries(statefullMap).map(([key, obj]) => {
			const isAllowedToBeNull = obj._allowsNull === true;
			const isInactive = isInactiveState(obj);

			if (isAllowedToBeNull && isInactive) {
				return [key, null];
			} else {
				return [key, obj.data];
			}
		}),
	) as {
		[K in keyof T]: T[K] extends { _allowsNull: true } ? T[K]["data"] : NonNullable<T[K]["data"]>;
	};

	return dataMap;
}
